import { createContext, useContext, useMemo, useRef } from 'react';
import type { ReactNode } from 'react';
import type { VerificationItem } from '@/components/verification-document/types';

export type VerificationItemsUpdate = (items: VerificationItem[]) => VerificationItem[];
type VerificationEditor = (update: VerificationItemsUpdate) => void;

type DealContextType = {
  dealId: string | undefined;
  /** Applies `update` to the open verification document. Returns false if none is open. */
  applyToOpenVerificationDocument: (update: VerificationItemsUpdate) => boolean;
  /** Called by the open verification document; returns a function that unregisters it. */
  registerVerificationEditor: (editor: VerificationEditor) => () => void;
};

const DealContext = createContext<DealContextType>({
  dealId: undefined,
  applyToOpenVerificationDocument: () => false,
  registerVerificationEditor: () => () => {},
});

export function DealProvider({
  children,
  dealId,
}: {
  children: ReactNode;
  dealId: string | undefined;
}) {
  const editorRef = useRef<VerificationEditor | null>(null);

  // useMemo is used to prevent the value from being recalculated on every render
  const value = useMemo<DealContextType>(() => ({
    dealId,
    applyToOpenVerificationDocument: (update) => {
      if (!editorRef.current)
        return false;
      editorRef.current(update);
      return true;
    },
    registerVerificationEditor: (editor) => {
      editorRef.current = editor;
      return () => {
        if (editorRef.current === editor)
          editorRef.current = null;
      };
    },
  }), [dealId]);

  return (
    <DealContext.Provider value={value}>
      {children}
    </DealContext.Provider>
  );
}

export function useDeal() {
  const context = useContext(DealContext);
  if (context === undefined) {
    throw new Error('useDeal must be used within a DealProvider');
  }
  return context;
}
